/**
 * LoginGate
 * ---------
 * Blocking modal shown after login. The user cannot use the app until
 * they acknowledge (checkbox required when `require_ack` is true).
 *
 * The acknowledgment is stored server-side with user + version + timestamp
 * as documentation, not just a UI checkbox.
 */
import { useState } from "react";
import { Button } from "../ui/button";
import { ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginGate({ announcement: a, onAcknowledge }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const requireAck = !!a.require_ack;
  const canContinue = !requireAck || checked;

  const handleContinue = async () => {
    if (!canContinue || submitting) return;
    setSubmitting(true);
    try {
      await onAcknowledge();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="announcement-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200">
        {/* Gold accent bar - matches Avida brand */}
        <div className="h-1.5" style={{ background: "linear-gradient(90deg,#FFDA00,#F4A500)" }} />

        {a._isTest && (
          <div className="bg-amber-50 border-b border-amber-200 px-5 py-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-amber-800">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Test preview — visible only to you · nothing is being saved
          </div>
        )}

        <div className="px-7 py-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#032866] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C7A99]">
                Announcement
              </div>
              <h2 className="text-lg font-bold text-[#032866] leading-tight">
                {a.title}
              </h2>
            </div>
          </div>

          {a.image_id && (
            <img
              src={`${API}/api/announcements/image/${a.image_id}`}
              alt=""
              className="w-full max-h-52 object-cover rounded-lg border border-slate-200 mb-4"
            />
          )}

          <p
            className="text-sm text-slate-700 whitespace-pre-line leading-relaxed"
            data-testid="announcement-gate-message"
          >
            {a.message}
          </p>

          {a.message_es && (
            <p className="text-xs text-slate-500 whitespace-pre-line leading-relaxed mt-3 pt-3 border-t border-slate-100 italic">
              {a.message_es}
            </p>
          )}

          {requireAck && (
            <label className="flex items-start gap-3 mt-5 p-3 rounded-lg bg-[#F6F8FA] border border-slate-200 cursor-pointer hover:border-[#032866]/40">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#032866]"
                data-testid="announcement-gate-checkbox"
              />
              <span className="text-xs text-slate-700 leading-snug">
                I understand and acknowledge this notice.
              </span>
            </label>
          )}

          <div className="mt-5 flex items-center justify-end">
            <Button
              onClick={handleContinue}
              disabled={!canContinue || submitting}
              className="h-10 px-6 bg-[#032866] hover:bg-[#0866FF] text-white rounded-xl text-sm font-semibold"
              data-testid="announcement-gate-continue"
            >
              {submitting ? "Saving…" : (a.button_label || "Continue")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
